<template>
    <form>
        <div class="row">
            <div v-for="(column, index) in rowColumns" :key="columns + index" :class="column">
                <div v-for="col in formColumns(index)" :key="col.name" :class="col.rowClass || rowClass" :style="containerStylesFor(col, model)">
                    <label :for="col.name" :class="col.labelClass || labelClass">
                        {{ col.label || col.name }}
                        <div v-if="col.description" :class="col.descriptionClass || descriptionClass">{{ col.description }}</div>
                    </label>
                    <!-- standard editors (text, checkbox, number) -->
                    <div v-if="isEditable(col) && !isBuiltinEditor(col.type) && !isComponent(col.type)" :class="col.fieldClass || fieldClass">
                        <input :type="inputType(col)" :class="getControlClass(col)" :placeholder="col.label || col.name" v-model="model[col.name]" v-bind="col.attr" @keypress="checkInput($event, col)" autocomplete="off" onfocus="this.select()">
                        <label v-if="col.type === 'bool' && col.formatter" class="align-top mt-1 ms-2">{{ col.formatter(model[col.name]) }}</label>
                        <div v-if="errors[col.name]" class="invalid-feedback">{{ errors[col.name] }}</div>
                    </div>
                    <!-- component -->
                    <div v-if="isEditable(col) && isComponent(col.type)" :class="col.fieldClass || fieldClass">
                        <keep-alive>
                            <component :is="col.type" :col="col" :model="model" v-model="model[col.name]"></component>
                        </keep-alive>
                    </div>
                    <!-- code -->
                    <div v-if="isEditable(col) && col.type === 'code'" :class="col.fieldClass || 'w-100'">
                        <code-editor v-model="model[col.name]" @input="model[col.name] = $event.target.value" font-size="16px" :header="false" height="auto" width="100%" :line-nums="true"
                                     max-height="90vh" padding="10px" border-radius=".375rem" theme="base16-materia"></code-editor>
                    </div>
                    <!-- color picker -->
                    <div v-if="isEditable(col) && col.type === 'color'" :class="col.fieldClass || 'col-sm-4'">
                        <input type="color" :class="getControlClass(col)" v-model="model[col.name]" v-bind="col.attr" autocomplete="off">
                        <div v-if="errors[col.name]" class="invalid-feedback">{{ errors[col.name] }}</div>
                    </div>
                    <div v-if="isEditable(col) && col.type === 'color'" :class="col.fieldClass || 'col-sm-4'">
                        <input type="text" class="form-control text-lowercase" v-model="model[col.name]" v-bind="col.attr" :placeholder="col.label || col.name" autocomplete="off" onfocus="this.select()">
                    </div>
                    <!-- dictionary -->
                    <div v-if="isEditable(col) && col.type === 'dictionary'" :class="col.fieldClass || fieldClass">
                        <dictionary-editor v-model="model[col.name]"></dictionary-editor>
                    </div>
                    <!-- tags -->
                    <div v-if="isEditable(col) && col.type === 'tags'" :class="col.fieldClass || fieldClass">
                        <tag-editor v-model="model[col.name]" :service="col.service ?? '/search'"></tag-editor>
                    </div>
                    <!-- dropdown -->
                    <div v-if="isEditable(col) && col.type === 'dropdown'" :class="col.fieldClass || fieldClass">
                        <select :class="getControlClass(col)" v-model="model[col.name]" v-bind="col.attr" :required="!!col.required">
                            <option v-if="!col.required">Select {{ col.label || col.name }}</option>
                            <option v-for="opt in col.options" :key="opt.value" :value="opt.value" v-html="Column.formattedOption(opt, col)"></option>
                        </select>
                        <div v-if="errors[col.name]" class="invalid-feedback">{{ errors[col.name] }}</div>
                    </div>
                    <!-- monthyear -->
                    <div v-if="isEditable(col) && col.type === 'monthyear'" :class="col.fieldClass || fieldClass">
                        <month-year-editor v-model="model[col.name]" :options="col.options" :required="!!col.required"></month-year-editor>
                    </div>
                    <!-- multiselect -->
                    <div v-if="isEditable(col) && col.type === 'multiselect'" :class="col.fieldClass || fieldClass">
                        <tag-editor v-model="model[col.name]" :service="col.service" :strings="!!col.strings" :options="col.options" :show-value="col.showValue ?? true" :show-on-focus="col.showOnFocus"></tag-editor>
                    </div>
                    <!-- long text -->
                    <div v-if="isEditable(col) && col.type === 'text'" :class="col.fieldClass || fieldClass">
                        <textarea :class="getControlClass(col)" v-model="model[col.name]" v-bind="col.attr" :required="!!col.required" :placeholder="col.label || col.name"></textarea>
                        <div v-if="errors[col.name]" class="invalid-feedback">{{ errors[col.name] }}</div>
                    </div>
                    <!-- password -->
                    <div v-if="isEditable(col) && col.type === 'password'" :class="col.fieldClass || fieldClass">
                        <input type="password" :class="getControlClass(col)" v-model="model[col.name]" v-bind="col.attr" :required="!!col.required" :placeholder="col.label || col.name" autocomplete="off" onfocus="this.select()">
                        <div v-if="errors[col.name]" class="invalid-feedback">{{ errors[col.name] }}</div>
                    </div>
                    <!-- phone -->
                    <div v-if="isEditable(col) && col.type === 'tel'" :class="col.fieldClass || fieldClass">
                        <input type="text" :class="getControlClass(col)" v-model="model[col.name]" v-bind="col.attr" :required="!!col.required" :placeholder="col.label || col.name" v-mask="telephoneMask" autocomplete="off" onfocus="this.select()">
                        <div v-if="errors[col.name]" class="invalid-feedback">{{ errors[col.name] }}</div>
                    </div>
                    <!-- range -->
                    <div v-if="isEditable(col) && col.type === 'range'" :class="col.fieldClass || fieldClass">
                        <div v-if="col.range?.length" class="row">
                            <div class="col text-start">{{col.range[0]}}</div>
                            <div class="col text-end">{{col.range[1]}}</div>
                        </div>
                        <input type="range" class="form-range" v-model="model[col.name]" v-bind="col.attr" :min="col.range[0]" :step="col.step || 1" :max="col.range[1]" :title="model[col.name]">
                    </div>
                    <!-- url/upload -->
                    <div v-if="isEditable(col) && (col.type === 'image' || col.type === 'url')" :class="col.fieldClass || fieldClass">
                        <div class="input-group has-validation">
                            <button v-if="model[col.name]" class="btn thumbnail" :style="{backgroundImage: 'url('+model[col.name]+')'}" @click.prevent="showPreview(model[col.name])"></button>
                            <input type="text" :class="getControlClass(col)" v-model="model[col.name]" v-bind="col.attr" :placeholder="col.label || col.name" autocomplete="off">
                            <label class="btn btn-outline-secondary">
                                Browse <input type="file" :name="col.name+'Upload'" hidden="" style="display:none" @change="onSelectedFile($event, model, col)">
                            </label>
                            <div v-if="errors[col.name]" class="invalid-feedback">{{ errors[col.name] }}</div>
                        </div>
                    </div>
                    <div v-if="!isEditable(col)" :class="col.fieldClass || fieldClass">
                        <div class="form-control" v-html="Column.formattedValue(model, col) || '&nbsp;'"></div>
                    </div>
                </div>
            </div>
        </div>
        <img v-if="previewImage" :src="previewImage" class="img-thumbnail position-absolute" style="top: 0;z-index: 10;left: 0;transform: translateY(50%);"
             @click="previewImage=null">
    </form>
</template>
<script>
    import CodeEditor from 'simple-code-editor';
    import DictionaryEditor from '../DictionaryEditor';
    import { IMaskDirective } from 'vue-imask';
    import MonthYearEditor from '../MonthYearEditor';
    import TagEditor from '../TagEditor';

    export const Column = {
        formattedOption(opt, col) {
            if (opt && col && col.name) {
                let value = opt.value;
                let entry = {};
                entry[col.name] = value;
                return Column.render(value, entry, col);
            }
        },
        formattedValue(entry, col) {
            if (entry && col && col.name) {
                let value = entry[col.name];
                return Column.render(value, entry, col);
            }
        },
        render(value, entry, col) {
            return col.renderer
                ? col.renderer(value, entry)
                : col.formatter
                    ? col.formatter(value, entry)
                    : col.type === 'dropdown' && col.options
                        ? col.options.find(x => x.value == value)?.text ?? value
                        : value;
        }
    };

    export const PickList = {
        aiModels: [
            ['chatgpt-4o-latest', 'ChatGPT 4o'],
            ['gpt-4o-2024-08-06', 'GPT 4o'],
            ['gpt-4o-mini-2024-07-18', 'GPT 4o mini'],
            ['gpt-4.1-2025-04-14', 'GPT 4.1'],
            ['o3-2025-04-16', 'o3'],
            ['o3-mini-2025-01-31', 'o3 mini'],
            ['o4-mini-2025-04-16', 'o4 mini'],
        ].map(e => { return { value: e[0], text: e[1] } }),
        languages: [
            ['cmn-CN', 'Chinese'],
            ['en-US', 'English'],
            ['fil-PH', 'Filipino'],
            ['fr-FR', 'French'],
            ['de-DE', 'German'],
            ['pt-BR', 'Portuguese'],
            ['es-ES', 'Spanish'],
            ['vi-VN', 'Vietnamese'],
        ].map(e => { return { value: e[0], text: e[1] } }),
        timeZones: [
            ['US Eastern Standard Time', '(UTC-05:00) Indiana (East)' ],
            ['Eastern Standard Time', '(UTC-05:00) Eastern Time (US & Canada)' ],
            ['Eastern Standard Time (Mexico)', '(UTC-05:00) Chetumal' ],
            ['Canada Central Standard Time', '(UTC-06:00) Saskatchewan' ],
            ['Central Standard Time (Mexico)', '(UTC-06:00) Guadalajara, Mexico City, Monterrey' ],
            ['Easter Island Standard Time', '(UTC-06:00) Easter Island' ],
            ['Central Standard Time', '(UTC-06:00) Central Time (US & Canada)' ],
            ['Central America Standard Time', '(UTC-06:00) Central America' ],
            ['Mountain Standard Time', '(UTC-07:00) Mountain Time (US & Canada)' ],
            ['Mountain Standard Time (Mexico)', '(UTC-07:00) Chihuahua, La Paz, Mazatlan' ],
            ['US Mountain Standard Time', '(UTC-07:00) Arizona' ],
            ['Pacific Standard Time', '(UTC-08:00) Pacific Time (US & Canada)' ],
            ['Pacific Standard Time (Mexico)', '(UTC-08:00) Baja California' ],
            ['Alaskan Standard Time', '(UTC-09:00) Alaska' ],
            ['Marquesas Standard Time', '(UTC-09:30) Marquesas Islands' ],
            ['Hawaiian Standard Time', '(UTC-10:00) Hawaii' ],
            ['Aleutian Standard Time', '(UTC-10:00) Aleutian Islands' ],
            ['Dateline Standard Time', '(UTC-12:00) International Date Line West' ],
            ['Line Islands Standard Time', '(UTC+14:00) Kiritimati Island' ],
            ['Samoa Standard Time', '(UTC+13:00) Samoa' ],
            ['Tonga Standard Time', '(UTC+13:00) Nuku\'alofa' ],
            ['UTC+13', '(UTC+13:00) Coordinated Universal Time+13' ],
            ['Chatham Islands Standard Time', '(UTC+12:45) Chatham Islands' ],
            ['Kamchatka Standard Time', '(UTC+12:00) Petropavlovsk-Kamchatsky - Old' ],
            ['Fiji Standard Time', '(UTC+12:00) Fiji' ],
            ['UTC+12', '(UTC+12:00) Coordinated Universal Time+12' ],
            ['New Zealand Standard Time', '(UTC+12:00) Auckland, Wellington' ],
            ['Russia Time Zone 11', '(UTC+12:00) Anadyr, Petropavlovsk-Kamchatsky' ],
            ['Central Pacific Standard Time', '(UTC+11:00) Solomon Is., New Caledonia' ],
            ['Sakhalin Standard Time', '(UTC+11:00) Sakhalin' ],
            ['Norfolk Standard Time', '(UTC+11:00) Norfolk Island' ],
            ['Magadan Standard Time', '(UTC+11:00) Magadan' ],
            ['Russia Time Zone 10', '(UTC+11:00) Chokurdakh' ],
            ['Bougainville Standard Time', '(UTC+11:00) Bougainville Island' ],
            ['Lord Howe Standard Time', '(UTC+10:30) Lord Howe Island' ],
            ['Vladivostok Standard Time', '(UTC+10:00) Vladivostok' ],
            ['Tasmania Standard Time', '(UTC+10:00) Hobart' ],
            ['West Pacific Standard Time', '(UTC+10:00) Guam, Port Moresby' ],
            ['AUS Eastern Standard Time', '(UTC+10:00) Canberra, Melbourne, Sydney' ],
            ['E. Australia Standard Time', '(UTC+10:00) Brisbane' ],
            ['AUS Central Standard Time', '(UTC+09:30) Darwin' ],
            ['Cen. Australia Standard Time', '(UTC+09:30) Adelaide' ],
            ['Yakutsk Standard Time', '(UTC+09:00) Yakutsk' ],
            ['Korea Standard Time', '(UTC+09:00) Seoul' ],
            ['North Korea Standard Time', '(UTC+09:00) Pyongyang' ],
            ['Tokyo Standard Time', '(UTC+09:00) Osaka, Sapporo, Tokyo' ],
            ['Transbaikal Standard Time', '(UTC+09:00) Chita' ],
            ['Aus Central W. Standard Time', '(UTC+08:45) Eucla' ],
            ['Ulaanbaatar Standard Time', '(UTC+08:00) Ulaanbaatar' ],
            ['Taipei Standard Time', '(UTC+08:00) Taipei' ],
            ['W. Australia Standard Time', '(UTC+08:00) Perth' ],
            ['Singapore Standard Time', '(UTC+08:00) Kuala Lumpur, Singapore' ],
            ['North Asia East Standard Time', '(UTC+08:00) Irkutsk' ],
            ['China Standard Time', '(UTC+08:00) Beijing, Chongqing, Hong Kong, Urumqi' ],
            ['Tomsk Standard Time', '(UTC+07:00) Tomsk' ],
            ['N. Central Asia Standard Time', '(UTC+07:00) Novosibirsk' ],
            ['North Asia Standard Time', '(UTC+07:00) Krasnoyarsk' ],
            ['W. Mongolia Standard Time', '(UTC+07:00) Hovd' ],
            ['Altai Standard Time', '(UTC+07:00) Barnaul, Gorno-Altaysk' ],
            ['SE Asia Standard Time', '(UTC+07:00) Bangkok, Hanoi, Jakarta' ],
            ['Myanmar Standard Time', '(UTC+06:30) Yangon (Rangoon)' ],
            ['Omsk Standard Time', '(UTC+06:00) Omsk' ],
            ['Bangladesh Standard Time', '(UTC+06:00) Dhaka' ],
            ['Central Asia Standard Time', '(UTC+06:00) Astana' ],
            ['Nepal Standard Time', '(UTC+05:45) Kathmandu' ],
            ['Sri Lanka Standard Time', '(UTC+05:30) Sri Jayawardenepura' ],
            ['India Standard Time', '(UTC+05:30) Chennai, Kolkata, Mumbai, New Delhi' ],
            ['Qyzylorda Standard Time', '(UTC+05:00) Qyzylorda' ],
            ['Pakistan Standard Time', '(UTC+05:00) Islamabad, Karachi' ],
            ['Ekaterinburg Standard Time', '(UTC+05:00) Ekaterinburg' ],
            ['West Asia Standard Time', '(UTC+05:00) Ashgabat, Tashkent' ],
            ['Afghanistan Standard Time', '(UTC+04:30) Kabul' ],
            ['Caucasus Standard Time', '(UTC+04:00) Yerevan' ],
            ['Volgograd Standard Time', '(UTC+04:00) Volgograd' ],
            ['Georgian Standard Time', '(UTC+04:00) Tbilisi' ],
            ['Saratov Standard Time', '(UTC+04:00) Saratov' ],
            ['Mauritius Standard Time', '(UTC+04:00) Port Louis' ],
            ['Russia Time Zone 3', '(UTC+04:00) Izhevsk, Samara' ],
            ['Azerbaijan Standard Time', '(UTC+04:00) Baku' ],
            ['Astrakhan Standard Time', '(UTC+04:00) Astrakhan, Ulyanovsk' ],
            ['Arabian Standard Time', '(UTC+04:00) Abu Dhabi, Muscat' ],
            ['Iran Standard Time', '(UTC+03:30) Tehran' ],
            ['E. Africa Standard Time', '(UTC+03:00) Nairobi' ],
            ['Russian Standard Time', '(UTC+03:00) Moscow, St. Petersburg' ],
            ['Belarus Standard Time', '(UTC+03:00) Minsk' ],
            ['Arab Standard Time', '(UTC+03:00) Kuwait, Riyadh' ],
            ['Turkey Standard Time', '(UTC+03:00) Istanbul' ],
            ['Arabic Standard Time', '(UTC+03:00) Baghdad' ],
            ['Namibia Standard Time', '(UTC+02:00) Windhoek' ],
            ['Libya Standard Time', '(UTC+02:00) Tripoli' ],
            ['Sudan Standard Time', '(UTC+02:00) Khartoum' ],
            ['Kaliningrad Standard Time', '(UTC+02:00) Kaliningrad' ],
            ['Israel Standard Time', '(UTC+02:00) Jerusalem' ],
            ['FLE Standard Time', '(UTC+02:00) Helsinki, Kyiv, Riga, Sofia, Tallinn, Vilnius' ],
            ['South Africa Standard Time', '(UTC+02:00) Harare, Pretoria' ],
            ['West Bank Standard Time', '(UTC+02:00) Gaza, Hebron' ],
            ['Syria Standard Time', '(UTC+02:00) Damascus' ],
            ['E. Europe Standard Time', '(UTC+02:00) Chisinau' ],
            ['Egypt Standard Time', '(UTC+02:00) Cairo' ],
            ['Middle East Standard Time', '(UTC+02:00) Beirut' ],
            ['GTB Standard Time', '(UTC+02:00) Athens, Bucharest' ],
            ['Jordan Standard Time', '(UTC+02:00) Amman' ],
            ['W. Central Africa Standard Time', '(UTC+01:00) West Central Africa' ],
            ['Central European Standard Time', '(UTC+01:00) Sarajevo, Skopje, Warsaw, Zagreb' ],
            ['Romance Standard Time', '(UTC+01:00) Brussels, Copenhagen, Madrid, Paris' ],
            ['Central Europe Standard Time', '(UTC+01:00) Belgrade, Bratislava, Budapest, Ljubljana, Prague' ],
            ['W. Europe Standard Time', '(UTC+01:00) Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna' ],
            ['Morocco Standard Time', '(UTC+01:00) Casablanca' ],
            ['Sao Tome Standard Time', '(UTC+00:00) Sao Tome' ],
            ['Greenwich Standard Time', '(UTC+00:00) Monrovia, Reykjavik' ],
            ['GMT Standard Time', '(UTC+00:00) Dublin, Edinburgh, Lisbon, London' ],
            ['UTC', '(UTC) Coordinated Universal Time' ],
            ['Cape Verde Standard Time', '(UTC-01:00) Cabo Verde Is.' ],
            ['Azores Standard Time', '(UTC-01:00) Azores' ],
            ['Mid-Atlantic Standard Time', '(UTC-02:00) Mid-Atlantic - Old' ],
            ['Bahia Standard Time', '(UTC-03:00) Salvador' ],
            ['Saint Pierre Standard Time', '(UTC-03:00) Saint Pierre and Miquelon' ],
            ['Magallanes Standard Time', '(UTC-03:00) Punta Arenas' ],
            ['Montevideo Standard Time', '(UTC-03:00) Montevideo' ],
            ['Greenland Standard Time', '(UTC-03:00) Greenland' ],
            ['Argentina Standard Time', '(UTC-03:00) City of Buenos Aires' ],
            ['SA Eastern Standard Time', '(UTC-03:00) Cayenne, Fortaleza' ],
            ['E. South America Standard Time', '(UTC-03:00) Brasilia' ],
            ['Tocantins Standard Time', '(UTC-03:00) Araguaina' ],
            ['Newfoundland Standard Time', '(UTC-03:30) Newfoundland' ],
            ['Pacific SA Standard Time', '(UTC-04:00) Santiago' ],
            ['SA Western Standard Time', '(UTC-04:00) Georgetown, La Paz, Manaus, San Juan' ],
            ['Central Brazilian Standard Time', '(UTC-04:00) Cuiaba' ],
            ['Venezuela Standard Time', '(UTC-04:00) Caracas' ],
            ['Atlantic Standard Time', '(UTC-04:00) Atlantic Time (Canada)' ],
            ['Paraguay Standard Time', '(UTC-04:00) Asuncion' ],
            ['Turks And Caicos Standard Time', '(UTC-05:00) Turks and Caicos' ],
            ['Cuba Standard Time', '(UTC-05:00) Havana' ],
            ['Haiti Standard Time', '(UTC-05:00) Haiti' ],
            ['SA Pacific Standard Time', '(UTC-05:00) Bogota, Lima, Quito, Rio Branco']
        ].map(e => { return { value: e[0], text: e[1] } }),
        voices: [
            ['GoogleCmnCnWavenetA', 'Anni', 'F', 'Chinese, Mandarin'],
            ['GoogleCmnCnWavenetB', 'Bo', 'M', 'Chinese, Mandarin'],
            ['GoogleCmnCnWavenetC', 'Caiwei', 'M', 'Chinese, Mandarin'],
            ['GoogleCmnCnWavenetD', 'Daiyu', 'F', 'Chinese, Mandarin'],
            ['GoogleCmnTwWavenetA', 'Amei', 'F', 'Chinese, Mandarin (Taiwan)'],
            ['GoogleCmnTwWavenetB', 'Biao', 'M', 'Chinese, Mandarin (Taiwan)'],
            ['GoogleCmnTwWavenetC', 'Cenhao', 'M', 'Chinese, Mandarin (Taiwan)'],
            ['GoogleDeDeNeural2A', 'Astrid', 'F', 'German, Germany'],
            ['GoogleDeDeNeural2B', 'Bern', 'M', 'German, Germany'],
            ['GoogleDeDeNeural2C', 'Claudia', 'F', 'German, Germany'],
            ['GoogleDeDeNeural2D', 'Dieter', 'M', 'German, Germany'],
            ['GoogleDeDeNeural2F', 'Frida', 'F', 'German, Germany'],
            ['GoogleDeDeWavenetA', 'Adele', 'F', 'German, Germany'],
            ['GoogleDeDeWavenetB', 'Bjorn', 'M', 'German, Germany'],
            ['GoogleDeDeWavenetC', 'Carina', 'F', 'German, Germany'],
            ['GoogleDeDeWavenetD', 'Derek', 'M', 'German, Germany'],
            ['GoogleDeDeWavenetE', 'Emil', 'M', 'German, Germany'],
            ['GoogleDeDeWavenetF', 'Gabi', 'F', 'German, Germany'],
            ['GoogleEnAuNeural2A', 'Ava', 'F', 'English, Australia'],
            ['GoogleEnAuNeural2B', 'Benjamin', 'M', 'English, Australia'],
            ['GoogleEnAuNeural2C', 'Claire', 'F', 'English, Australia'],
            ['GoogleEnAuNeural2D', 'Dylan', 'M', 'English, Australia'],
            ['GoogleEnAuWavenetA', 'Amber', 'F', 'English, Australia'],
            ['GoogleEnAuWavenetB', 'Bradley', 'M', 'English, Australia'],
            ['GoogleEnAuWavenetC', 'Cara', 'F', 'English, Australia'],
            ['GoogleEnAuWavenetD', 'Donald', 'M', 'English, Australia'],
            ['GoogleEnGbNeural2A', 'Alice', 'F', 'English, UK'],
            ['GoogleEnGbNeural2B', 'Bryson', 'M', 'English, UK'],
            ['GoogleEnGbNeural2C', 'Casey', 'F', 'English, UK'],
            ['GoogleEnGbNeural2D', 'Darren', 'M', 'English, UK'],
            ['GoogleEnGbNeural2F', 'Fern', 'F', 'English, UK'],
            ['GoogleEnGbWavenetA', 'Avery', 'F', 'English, UK'],
            ['GoogleEnGbWavenetB', 'Benedict', 'M', 'English, UK'],
            ['GoogleEnGbWavenetC', 'Charlotte', 'F', 'English, UK'],
            ['GoogleEnGbWavenetD', 'Donovan', 'M', 'English, UK'],
            ['GoogleEnGbWavenetF', 'Fiona', 'F', 'English, UK'],
            ['GoogleEnInNeural2A', 'Ana', 'F', 'English, India'],
            ['GoogleEnInNeural2B', 'Badri', 'M', 'English, India'],
            ['GoogleEnInNeural2C', 'Chetan', 'M', 'English, India'],
            ['GoogleEnInNeural2D', 'Diya', 'F', 'English, India'],
            ['GoogleEnInWavenetA', 'Sanah', 'F', 'English, India'],
            ['GoogleEnInWavenetB', 'Bharat', 'M', 'English, India'],
            ['GoogleEnInWavenetC', 'Chandan', 'M', 'English, India'],
            ['GoogleEnInWavenetD', 'Divya', 'F', 'English, India'],
            ['GoogleEnUsNeural2A', 'Aaron', 'M', 'English, US'],
            ['GoogleEnUsNeural2C', 'Chloe', 'F', 'English, US'],
            ['GoogleEnUsNeural2D', 'Dawson', 'M', 'English, US'],
            ['GoogleEnUsNeural2E', 'Ellie', 'F', 'English, US'],
            ['GoogleEnUsNeural2F', 'Faith', 'F', 'English, US'],
            ['GoogleEnUsNeural2G', 'Georgia', 'F', 'English, US'],
            ['GoogleEnUsNeural2H', 'Heather', 'F', 'English, US'],
            ['GoogleEnUsNeural2I', 'Ian', 'M', 'English, US'],
            ['GoogleEnUsNeural2J', 'Jason', 'M', 'English, US'],
            ['GoogleEnUsWavenetA', 'Andor', 'M', 'English, US'],
            ['GoogleEnUsWavenetB', 'Bryce', 'M', 'English, US'],
            ['GoogleEnUsWavenetC', 'Caitlin', 'F', 'English, US'],
            ['GoogleEnUsWavenetD', 'Declan', 'M', 'English, US'],
            ['GoogleEnUsWavenetE', 'Eliana', 'F', 'English, US'],
            ['GoogleEnUsWavenetF', 'Farah', 'F', 'English, US'],
            ['GoogleEnUsWavenetG', 'Gemma', 'F', 'English, US'],
            ['GoogleEnUsWavenetH', 'Helen', 'F', 'English, US'],
            ['GoogleEnUsWavenetI', 'Isaiah', 'M', 'English, US'],
            ['GoogleEnUsWavenetJ', 'Jasper', 'M', 'English, US'],
            ['GoogleEsEsNeural2A', 'Aldea', 'F', 'Spanish, Spain'],
            ['GoogleEsEsNeural2B', 'Beto', 'M', 'Spanish, Spain'],
            ['GoogleEsEsNeural2C', 'Catalina', 'F', 'Spanish, Spain'],
            ['GoogleEsEsNeural2D', 'Delia', 'F', 'Spanish, Spain'],
            ['GoogleEsEsNeural2E', 'Elena', 'F', 'Spanish, Spain'],
            ['GoogleEsEsNeural2F', 'Felipe', 'M', 'Spanish, Spain'],
            ['GoogleEsEsWavenetB', 'Bruno', 'M', 'Spanish, Spain'],
            ['GoogleEsEsWavenetC', 'Carmen', 'F', 'Spanish, Spain'],
            ['GoogleEsEsWavenetD', 'Dora', 'F', 'Spanish, Spain'],
            ['GoogleEsUsNeural2A', 'Aida', 'F', 'Spanish, US'],
            ['GoogleEsUsNeural2B', 'Bernardo', 'M', 'Spanish, US'],
            ['GoogleEsUsNeural2C', 'Carlos', 'M', 'Spanish, US'],
            ['GoogleEsUsWavenetA', 'Anabel', 'F', 'Spanish, US'],
            ['GoogleEsUsWavenetB', 'Bartolo', 'M', 'Spanish, US'],
            ['GoogleEsUsWavenetC', 'Cristian', 'M', 'Spanish, US'],
            ['GoogleFilPhWavenetA', 'Alma', 'F', 'Filipino'],
            ['GoogleFilPhWavenetB', 'Bituin', 'F', 'Filipino'],
            ['GoogleFilPhWavenetC', 'Crisanto', 'M', 'Filipino'],
            ['GoogleFilPhWavenetD', 'Dakila', 'M', 'Filipino'],
            ['GoogleFrCaNeural2A', 'Andrea', 'F', 'French, Canada'],
            ['GoogleFrCaNeural2B', 'Bastien', 'M', 'French, Canada'],
            ['GoogleFrCaNeural2C', 'Coraline', 'F', 'French, Canada'],
            ['GoogleFrCaNeural2D', 'Didier', 'M', 'French, Canada'],
            ['GoogleFrCaWavenetA', 'Lilou', 'F', 'French, Canada'],
            ['GoogleFrCaWavenetB', 'Benoit', 'M', 'French, Canada'],
            ['GoogleFrCaWavenetC', 'Flora', 'F', 'French, Canada'],
            ['GoogleFrCaWavenetD', 'Damien', 'M', 'French, Canada'],
            ['GoogleFrFrNeural2A', 'Aurelie', 'F', 'French, France'],
            ['GoogleFrFrNeural2B', 'Bertrand', 'M', 'French, France'],
            ['GoogleFrFrNeural2C', 'Françoise', 'F', 'French, France'],
            ['GoogleFrFrNeural2D', 'Danton', 'M', 'French, France'],
            ['GoogleFrFrNeural2E', 'Emilie', 'F', 'French, France'],
            ['GoogleFrFrWavenetA', 'Fleur', 'F', 'French, France'],
            ['GoogleFrFrWavenetB', 'Jean-Claude', 'M', 'French, France'],
            ['GoogleFrFrWavenetC', 'Madelaine', 'F', 'French, France'],
            ['GoogleFrFrWavenetD', 'Edmond', 'M', 'French, France'],
            ['GoogleFrFrWavenetE', 'Marie', 'F', 'French, France'],
            ['GooglePtBrNeural2A', 'Gloriana', 'F', 'Portuguese, Brazil'],
            ['GooglePtBrNeural2B', 'Marcio', 'M', 'Portuguese, Brazil'],
            ['GooglePtBrNeural2C', 'Melina', 'F', 'Portuguese, Brazil'],
            ['GooglePtBrWavenetB', 'Luiz', 'M', 'Portuguese, Brazil'],
            ['GooglePtBrWavenetC', 'Paula', 'F', 'Portuguese, Brazil'],
            ['GooglePtPtWavenetA', 'Raquel', 'F', 'Portuguese, Portugal'],
            ['GooglePtPtWavenetB', 'Ricardo', 'M', 'Portuguese, Portugal'],
            ['GooglePtPtWavenetC', 'Hugo', 'M', 'Portuguese, Portugal'],
            ['GooglePtPtWavenetD', 'Nadia', 'F', 'Portuguese, Portugal'],
            ['GoogleViVnWavenetA', 'Thi', 'F', 'Vietnamese'],
            ['GoogleViVnWavenetB', 'Phi', 'M', 'Vietnamese'],
            ['GoogleViVnWavenetC', 'Anh', 'F', 'Vietnamese'],
            ['GoogleViVnWavenetD', 'Gia', 'M', 'Vietnamese'],
            ['PollyAmyNeural', 'Amy', 'F', 'English, UK'],
            ['PollyAndresNeural', 'Andrés', 'M', 'Spanish, Mexico'],
            ['PollyAriaNeural', 'Aria', 'F', 'English, New Zealand'],
            ['PollyArthurNeural', 'Arthur', 'M', 'English, UK'],
            ['PollyAyandaNeural', 'Ayanda', 'F', 'English, South African'],
            ['PollyBrianNeural', 'Brian', 'M', 'English, UK'],
            ['PollyCamilaNeural', 'Camila', 'F', 'Portuguese, Brazil'],
            ['PollyDanielleNeural', 'Danielle', 'F', 'English, US'],
            ['PollyDanielNeural', 'Daniel', 'M', 'German, Germany'],
            ['PollyEmmaNeural', 'Emma', 'F', 'English, UK'],
            ['PollyGabrielleNeural', 'Gabrielle', 'F', 'French, Canada'],
            ['PollyGregoryNeural', 'Gregory', 'M', 'English, US'],
            ['PollyHannahNeural', 'Hannah', 'F', 'German, Austria'],
            ['PollyHiujinNeural', 'Hiujin', 'F', 'Chinese, Cantonese'],
            ['PollyInesNeural', 'Inês', 'F', 'Portuguese, Portugal'],
            ['PollyIsabelleNeural', 'Isabelle', 'F', 'French, Belgium'],
            ['PollyIvyNeural', 'Ivy', 'Female (child)', 'English, US'],
            ['PollyJoannaNeural', 'Joanna', 'F', 'English, US'],
            ['PollyJoeyNeural', 'Joey', 'M', 'English, US'],
            ['PollyJustinNeural', 'Justin', 'Male (child)', 'English, US'],
            ['PollyKajalNeural', 'Kajal', 'F', 'English, India'],
            ['PollyKendraNeural', 'Kendra', 'F', 'English, US'],
            ['PollyKevinNeural', 'Kevin', 'Male (child)', 'English, US'],
            ['PollyKimberlyNeural', 'Kimberly', 'F', 'English, US'],
            ['PollyLeaNeural', 'Léa', 'F', 'French, France'],
            ['PollyLiamNeural', 'Liam', 'M', 'French, Canada'],
            ['PollyLuciaNeural', 'Lucia', 'F', 'Spanish, Spain'],
            ['PollyLupeNeural', 'Lupe', 'F', 'Spanish, US'],
            ['PollyMatthewNeural', 'Matthew', 'M', 'English, US'],
            ['PollyMiaNeural', 'Mia', 'F', 'Spanish, Mexico'],
            ['PollyNiamhNeural', 'Niamh', 'F', 'English, Ireland'],
            ['PollyOliviaNeural', 'Olivia', 'F', 'English, Australia'],
            ['PollyPedroNeural', 'Pedro', 'M', 'Spanish, US'],
            ['PollyRemiNeural', 'Rémi', 'M', 'French, France'],
            ['PollyRuthNeural', 'Ruth', 'F', 'English, US'],
            ['PollySalliNeural', 'Salli', 'F', 'English, US'],
            ['PollySergioNeural', 'Sergio', 'M', 'Spanish, Spain'],
            ['PollyStephenNeural', 'Stephen', 'M', 'English, US'],
            ['PollyThiagoNeural', 'Thiago', 'M', 'Portuguese, Brazil'],
            ['PollyVickiNeural', 'Vicki', 'F', 'German, Germany'],
            ['PollyVitoriaNeural', 'Vitória', 'F', 'Portuguese, Brazil'],
            ['PollyZhiyuNeural', 'Zhiyu', 'F', 'Chinese, Mandarin']
        ].map(e => { return { value: e[0], text: e[1], meta: e[2], lang: e[3] } }),
    };

    export default {
        emits: ['update:model-value', 'onUpload'],
        props: {
            modelValue: {
                type: Object
            },
            columnCount: {
                type: Number,
                default: () => 1
            },
            columns: {
                type: Array,
                default: () => []
            },
            descriptionClass: {
                type: String,
                default: () => 'small text-muted'
            },
            errors: {
                type: Object,
                default: () => {
                    return {};
                }
            },
            fieldClass: {
                type: String,
                default: () => 'col-sm-8'
            },
            labelClass: {
                type: String,
                default: () => 'col-sm-4 col-form-label'
            },
            rowClass: {
                type: String,
                default: () => 'row g-2 mb-3'
            },
        },
        data: function () {
            return {
                model: {},
                previewImage: null,
                Column
            };
        },
        components: {
            CodeEditor,
            DictionaryEditor,
            MonthYearEditor,
            TagEditor,
        },
        computed: {
            rowColumns() {
                switch (this.columnCount) {
                    case 3: return ['col-md-4', 'col-md-4', 'col-md-4'];
                    case 2: return ['col-md-6', 'col-md-6'];
                    case 1:
                    default:
                        return ['col'];
                }
            },
            telephoneMask() {
                return {
                    mask: '+{1} (000) 000-0000',
                };
            },
        },
        directives: {
            mask: IMaskDirective
        },
        methods: {
            checkInput(event, col) {
                if (col.allowInput) {
                    if (!col.allowInput(event, col)) {
                        event.preventDefault();
                        return;
                    }
                }
                else if (col.type == 'number' && ![43, 44, 45, 46, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57].includes(event.keyCode)) {
                    event.preventDefault();
                }
            },
            clear(defaults) {
                let vm = this;
                defaults = defaults ?? {};
                //TODO: lodash can go away
                _.forOwn(vm.model, (v, k) => {
                    vm.model[k] = null;
                });
                vm.columns.forEach((col) => {
                    let defaultValue = defaults[col.name] ?? col.defaultValue;
                    vm.model[col.name] = (col.type === 'dropdown' && col.required)
                        ? defaultValue ?? col.options[0].value
                        : col.type === 'bool'
                            ? defaultValue ?? false
                            : defaultValue ?? null;
                });
            },
            containerStylesFor(col, model) {
                const styles = {};
                if (col.hiddenWhen instanceof Function && col.hiddenWhen(model, col)) {
                    styles.display = 'none';
                    return styles;
                }
            },
            formColumns(index) {
                const visible = this.columns.filter((c) => !c.hidden || (Array.isArray(c.hidden) && !c.hidden.includes('form')));
                const page = Math.ceil(visible.length / (Math.abs(this.columnCount) || 1));
                const start = index * page;
                const end = start + page;
                return visible.filter((_, i) => i >= start && i < end);
            },
            getControlClass(col) {
                const a = ['form-control'];
                if (this.errors && col && this.errors[col.name]) a.push('is-invalid');
                return a;
            },
            inputType(col) {
                switch (col.type) {
                    case 'bool': return 'checkbox';
                }
                return col.type || 'text';
            },
            isBuiltinEditor(type) {
                return ['code', 'color', 'dictionary', 'dropdown', 'image', 'monthyear', 'multiselect', 'password', 'range', 'tags', 'tel', 'text', 'url']
                    .includes(type);
            },
            isComponent(type) {
                return type && typeof (type) === 'object';
            },
            isEditable(col) {
                if (col.editable === false) return false;
                if (typeof (col.editable) === 'function') {
                    return col.editable(col, this.model);
                }
                return true;
            },
            onSelectedFile(event, model, col) {
                this.$emit('onUpload', event, model, col);
            },
            showPreview(url) {
                this.previewImage = url;
            },
        },
        created() {
            if (this.modelValue) {
                this.model = this.modelValue;
            }
        },
        watch: {
            model: {
                deep: true,
                handler(model) {
                    this.$emit('update:model-value', model);
                }
            },
            modelValue(value) {
                this.model = value;
            }
        },
    };
</script>
